<template>
    <div class="container mortgage-calculator" :class="calculatorName">

		<div class="row justify-content-center calculator-header">
			<CalculatorHeader>
				<h2>Help to Buy Mortgage Calculator</h2>
				<h4>Fill out a few details to see if you are eligible for Help to Buy</h4>
			</CalculatorHeader>
		</div>
		<div class="row justify-content-center calculator-body">
			<div class="col-md-10 col-xl-8 mb-3">
				<form class="calculator-container">

					<div class="mt-5" @keyup="resetCalculation" @change="resetCalculation">
	
						<h4 class="calc-fieldset-title">Applicant Details</h4>

						<BooleanChoiceButtons
							fieldText="Number of applicants"
							buttonTextLeft="One applicant"
							buttonTextRight="Two applicants"							
						>
							<template v-slot:left>
								<input type="radio" v-model="applicantsCount" :value="1">								
							</template>
							<template v-slot:right>
								<input type="radio" v-model="applicantsCount" :value="2">								
							</template>																
						</BooleanChoiceButtons>						
						
						<transition name="fade-x" mode="out-in">
							<InputElement
								v-if="applicantsCount"
								fieldText="Applicant 1 annual income (including benefits)?"
							>
								<InputTypeNumber currencyPlaceholder v-model="annualIncome1"/>
							</InputElement>
						</transition>
						
						<transition name="fade-x" mode="out-in">
							<InputElement
								v-if="applicantsCount == 2"
								fieldText="Applicant 2 annual income (including benefits)?"
							>
								<InputTypeNumber currencyPlaceholder v-model="annualIncome2" />
							</InputElement>
						</transition>

						<transition name="scale-in" mode="out-in">
							<ReturnedValueBox :innerElement=true v-if="totalHouseholdIncome > 0" text="Your household income is: " :value="totalHouseholdIncome" />
						</transition>

						<transition name="fade-x" mode="out-in">
							<div v-if="totalHouseholdIncome > 0">
								
								<h4 class="calc-fieldset-title">Property Details</h4>

								<InputElement
									class="description-column"
									fieldText="<div>What is the property purchase price?</div><div style='font-size:small;'>(Max £600,000 please <a href='https://www.justmortgagebrokers.co.uk/contact/'>speak to us</a> for more details)</div>"
								>
									<InputTypeNumber currencyPlaceholder v-model="housePrice" :max="600000"/>
								</InputElement>

								<ReturnedValueBox
									v-if="housePrice > 0"
									:innerElement=true
									text="You will need a minimum deposit of: "
									:value="minDeposit"
								/>											

								<transition name="fade-x" mode="out-in">
									<BooleanChoiceButtons
										v-if="housePrice > 0"
										fieldText="Is this property in any Borough of London?"
										buttonTextLeft="Yes"
										buttonTextRight="No"								
									>
										<template v-slot:left>
											<input type="radio" value="Yes" v-model="propertyInLondon">								
										</template>
										<template v-slot:right>
											<input type="radio" value="No" v-model="propertyInLondon">								
										</template>																
									</BooleanChoiceButtons>
								</transition>
								
								<transition name="fade-x" mode="out-in">
									<div
										v-if="housePrice > 0 && propertyInLondon !== ''"
										class="mb-5"	
									>
										<InputElement
											
											fieldText="Please enter the first part of the property postcode"
										>
											<input class="form-control calc-form-control" type="text" v-model="postcode" placeholder="e.g. IP1 or NR22">
										</InputElement>

									</div>
								</transition>				
								
								<transition name="fade-x" mode="out-in">
									<div
										v-if="housePrice > 0 && propertyInLondon !== '' && postcode !== ''"
										class="mb-5"
									>
										<InputElement
											fieldText="How much extra over the minimum deposit can you provide towards the purchase price?"
										>
											<InputTypeNumber currencyPlaceholder v-model="extraDeposit" />
										</InputElement>

										<ReturnedValueBox :innerElement=true text="You will need to borrow: " :value="requestedAmountToBorrow" />
									</div>
								</transition>

								<transition name="fade-x" mode="out-in">
									<InputElement
										v-if="housePrice > 0 && propertyInLondon !== '' && postcode !== '' && extraDeposit !== ''"
										fieldText="Please confirm any monthly payments towards loan or HP payments that will remain after your purchase"
									>
										<InputTypeNumber currencyPlaceholder v-model="loanPayments" />
									</InputElement>		
								</transition>

								<transition name="fade-x" mode="out-in">
									<InputElement
										v-if="housePrice > 0 && propertyInLondon !== '' && postcode !== '' && extraDeposit !== '' && loanPayments !== ''"
										fieldText="Please confirm any credit card balances that will remain after your purchase"
									>
										<InputTypeNumber currencyPlaceholder v-model="creditBalance" />
									</InputElement>
								</transition>

								<transition name="scale-in" mode="out-in">
									<div
										v-if="showCalculateButton"
										class="text-center mt-5 mb-3"
									>
										<div class="btn calc-btn-secondary" @click="calculate">CALCULATE</div>
									</div>
								</transition>

							</div>
						</transition>

					</div>

					<transition name="fade-y" mode="out-in">
						<div v-if="calcCompleted">

							<ResultsContainer class="full-height calc-results-box">
								<div class="mb-3 results-title">You would like to borrow from us:</div>
								<span class="fcs-price">£{{ formatNumberOutput(requestedAmountToBorrow) }}</span>
								<div class="mb-3">{{this.chance}}</div>
							</ResultsContainer>

							<SubmissionForm :calculatorName="calculatorName" :calculatorData="collateMessageBody" />

						</div>
					</transition>						
				</form>
			</div>				
		</div>
		
    </div>
</template>

<script>
import BooleanChoiceButtons from '@/components/utilities/booleanChoiceButtons.vue'
import CalculatorHeader from '@/components/utilities/calculatorHeader.vue'
import InputElement from '@/components/utilities/inputElement.vue'
import InputTypeNumber from '@/components/utilities/inputTypeNumber.vue'
import ResultsContainer from '@/components/utilities/resultsContainer.vue'
import ReturnedValueBox from '@/components/utilities/returnedValueBox.vue'
import SubmissionForm from '@/components/utilities/submissionForm.vue'

export default {
	components: {
		BooleanChoiceButtons,
		CalculatorHeader,	
		InputElement,
		InputTypeNumber,
		ResultsContainer,
		ReturnedValueBox,
		SubmissionForm
	},
    data() {
        return {
			calculatorName: 'help-to-buy-calculator',
            applicantsCount: null,
            annualIncome1: null,
            annualIncome2: null,

			housePrice: null,
            propertyInLondon: '',
            postcode: '',
            extraDeposit: '',
            loanPayments: '',
            creditBalance: null,
			chance: '',
			calcCompleted: false
        }
    },
    methods: {
        calculate() {
			const annualIncome = this.multiplyUsableAnnualIncome
			const amountToBorrow = this.requestedAmountToBorrow
			if(annualIncome >= amountToBorrow) {
				this.chance = `You've got a very good chance of getting a Help to Buy Mortgage, contact us for further details`;
			}
			if((annualIncome < amountToBorrow) && (annualIncome > amountToBorrow - 10000)) {
				this.chance = `We may be able to help you get a Help to Buy Mortgage, contact us for further details.`;
			}
			if((annualIncome < amountToBorrow - 10000) && (annualIncome > amountToBorrow - 20000)) {
				this.chance = `We should be able to help but we will need more details to be sure, contact us for further details.`;
			}
			if(annualIncome < amountToBorrow - 20000) {
				this.chance = `We need to take a closer look to confirm that you are eligible, contact us for further details.`;
			}
			this.calcCompleted = true;
        },
		clearData() {
			this.housePrice = null,
			this.propertyInLondon = '',
			this.postcode = '',
			this.extraDeposit = '',
			this.loanPayments = '',
			this.creditBalance = null,
			this.chance = '',
			this.calcCompleted = false
		},	
		formatNumberOutput(number) {
			const newNumber = (new Intl.NumberFormat('en-GB').format(number))
			if ( newNumber === 'NaN' ) { return 0 } return newNumber
		},		
        removeHyphensToUpperCase(str) {
            return str.replace(/(-|^)([^-]?)/g, function(_, prep, letter) { return (prep && ' ') + letter.toUpperCase(); });
		},
		resetCalculation() {
			this.calcCompleted = false;
		},
		formatEmptyMessageFields(message){
			return message ? message : 'N/A'
		}					
    },	
    computed: {
		/**
		 * Show button if previous section is completed
		 * @return {Bool}
		 */		
		showCalculateButton() {
			let calculateButton
			(this.housePrice > 0 && this.propertyInLondon !== '' && this.postcode !== '' && this.extraDeposit !== '' && this.loanPayments !== '')
				? calculateButton = true
				: calculateButton = false;
			return calculateButton			
		},		
        minDeposit() {
            let calculatedHousePrice = this.housePrice ? this.housePrice : 0;
			return (calculatedHousePrice / 100) * 5;
        },
        totalHouseholdIncome() {
            let annualIncome1 = this.annualIncome1 ? this.annualIncome1 : 0;
            let annualIncome2 = this.annualIncome2 ? this.annualIncome2 : 0;
			let totalIncome = annualIncome1 + annualIncome2;

			if (!totalIncome > 0) { this.clearData() }

            return totalIncome;
        },
        requestedAmountToBorrow() {
			let calculatedHousePrice = this.housePrice ? this.housePrice : 0;
			let calculatedExtraDeposit = this.extraDeposit ? this.extraDeposit : 0;
			
			return ((calculatedHousePrice / 100) * 80) - this.minDeposit - calculatedExtraDeposit;
        },		
		usableAnnualIncome() {
			let calculatedLoanPayments = this.loanPayments ? this.loanPayments : 0;
			let calculatedCreditBalance = this.creditBalance ? this.creditBalance : 0;

			return this.totalHouseholdIncome - ( (calculatedLoanPayments * 12) + ((( calculatedCreditBalance / 100 ) * 2.5 ) * 12) );
		},
		multiplyUsableAnnualIncome() {
			return this.usableAnnualIncome * 5;
		},		
		collateMessageBody() {
			const calculatorData = {
				calculatorTitle: this.removeHyphensToUpperCase(this.calculatorName),
				calculatorResults:
				`
					Number of applicants: ${this.applicantsCount}
					Applicant 1 income: £${this.formatEmptyMessageFields(this.annualIncome1)}
					Applicant 2 income: £${this.formatEmptyMessageFields(this.annualIncome2)}
					Household income: £${this.totalHouseholdIncome}
					Property purchase price: £${this.housePrice}
					Is property in London? ${this.propertyInLondon}
					Postcode: ${this.postcode}
					Minimum deposit needed: £${this.minDeposit.toFixed(2)}
					Extra deposit available: £${this.extraDeposit}
					Total amount to be borrowed: £${this.requestedAmountToBorrow}					
					Loan or HP monthly payments: £${this.loanPayments}
					Credit balances: £${this.creditBalance}
					Usable annual income: £${this.usableAnnualIncome}
				`
			}
			
			return calculatorData;
		}		
    }
}
</script>

<style lang="scss">
	@import './styles/calcs.scss';
</style>